import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { SpaBadge } from '@/components/ui/spa-badge';
import { useToast } from '@/hooks/use-toast';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { format } from 'date-fns';
import { OverviewTab } from '@/components/admin/OverviewTab';
import { ProgramsTab } from '@/components/admin/ProgramsTab';
import { DocumentsTab } from '@/components/admin/DocumentsTab';
import { EventsTab } from '@/components/admin/EventsTab';
import { DiasporaTab } from '@/components/admin/DiasporaTab';

interface Company {
  id: string;
  name: string;
  country: string;
  spa_linked: boolean;
  verified: boolean;
  created_at: string;
}

interface Opportunity {
  id: string;
  title: string;
  type: string;
  sector: string;
  spa_linked: boolean;
  status: string;
  company: { name: string };
  created_at: string;
}

export default function AdminDashboard() {
  const { t } = useTranslation();
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && (!user || role !== 'admin')) {
      navigate('/auth');
      return;
    }

    if (user && role === 'admin') {
      fetchData();
    }
  }, [user, role, authLoading, navigate]);

  const fetchData = async () => {
    try {
      // Fetch unverified companies
      const { data: companiesData } = await supabase
        .from('companies')
        .select('*')
        .eq('verified', false)
        .order('created_at', { ascending: false });

      setCompanies(companiesData || []);

      // Fetch opportunities pending review
      const { data: oppsData } = await supabase
        .from('opportunities')
        .select('*, company:companies(name)')
        .in('status', ['draft'])
        .order('created_at', { ascending: false });

      setOpportunities((oppsData as unknown as Opportunity[]) || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const approveCompany = async (companyId: string) => {
    setActionLoading(companyId);
    try {
      const { error } = await supabase
        .from('companies')
        .update({ verified: true })
        .eq('id', companyId);

      if (error) throw error;

      setCompanies(companies.filter(c => c.id !== companyId));
      toast({ title: t('misc.companyApproved') });
    } catch (error) {
      toast({ title: t('misc.companyApproveError'), variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  const approveOpportunity = async (oppId: string) => {
    setActionLoading(oppId);
    try {
      const { error } = await supabase
        .from('opportunities')
        .update({ status: 'open', published_at: new Date().toISOString() })
        .eq('id', oppId);

      if (error) throw error;

      setOpportunities(opportunities.filter(o => o.id !== oppId));
      toast({ title: t('misc.opportunityApproved') });
    } catch (error) {
      toast({ title: t('misc.opportunityApproveError'), variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="container flex min-h-[50vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container py-8">
        <h1 className="mb-8 text-3xl font-bold">{t('admin.dashboard.title')}</h1>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="flex-wrap">
            <TabsTrigger value="overview">{t('admin.tabs.overview')}</TabsTrigger>
            <TabsTrigger value="companies">
              {t('admin.dashboard.companiesTab')}
              {companies.length > 0 && (
                <Badge variant="secondary" className="ml-2">{companies.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="opportunities">
              {t('admin.dashboard.opportunitiesTab')}
              {opportunities.length > 0 && (
                <Badge variant="secondary" className="ml-2">{opportunities.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="programs">{t('admin.tabs.programs')}</TabsTrigger>
            <TabsTrigger value="documents">{t('admin.tabs.documents')}</TabsTrigger>
            <TabsTrigger value="events">{t('admin.tabs.events')}</TabsTrigger>
            <TabsTrigger value="diaspora">{t('admin.tabs.diaspora')}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <OverviewTab />
          </TabsContent>

          <TabsContent value="companies">
            <Card>
              <CardHeader>
                <CardTitle>{t('admin.dashboard.companiesTab')}</CardTitle>
              </CardHeader>
              <CardContent>
                {companies.length === 0 ? (
                  <p className="text-muted-foreground">{t('admin.columns.noPendingCompanies', 'No companies pending verification.')}</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                         <TableHead>{t('admin.columns.name')}</TableHead>
                        <TableHead>{t('admin.columns.country')}</TableHead>
                        <TableHead>{t('admin.columns.spaLinked')}</TableHead>
                        <TableHead>{t('admin.columns.created')}</TableHead>
                        <TableHead>{t('admin.columns.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {companies.map((company) => (
                        <TableRow key={company.id}>
                          <TableCell className="font-medium">{company.name}</TableCell>
                          <TableCell>{company.country}</TableCell>
                          <TableCell>
                            {company.spa_linked ? <SpaBadge size="sm" /> : '-'}
                          </TableCell>
                          <TableCell>
                            {format(new Date(company.created_at), 'MMM d, yyyy')}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => approveCompany(company.id)}
                                disabled={actionLoading === company.id}
                              >
                                {actionLoading === company.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <CheckCircle className="mr-1 h-4 w-4" />
                                )}
                                {t('admin.dashboard.approve')}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(`/companies/${company.id}`)}
                              >
                                {t('admin.dashboard.viewProfile')}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="opportunities">
            <Card>
              <CardHeader>
                <CardTitle>{t('admin.dashboard.opportunitiesTab')}</CardTitle>
              </CardHeader>
              <CardContent>
                {opportunities.length === 0 ? (
                  <p className="text-muted-foreground">{t('admin.columns.noPendingOpportunities', 'No opportunities pending review.')}</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                         <TableHead>{t('admin.columns.title')}</TableHead>
                        <TableHead>{t('admin.columns.company')}</TableHead>
                        <TableHead>{t('admin.columns.type')}</TableHead>
                        <TableHead>{t('admin.columns.sector')}</TableHead>
                        <TableHead>{t('admin.columns.spa')}</TableHead>
                        <TableHead>{t('admin.columns.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {opportunities.map((opp) => (
                        <TableRow key={opp.id}>
                          <TableCell className="font-medium">{opp.title}</TableCell>
                          <TableCell>{opp.company?.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{t(`opportunities.types.${opp.type}`)}</Badge>
                          </TableCell>
                          <TableCell>{t(`sectors.${opp.sector}`)}</TableCell>
                          <TableCell>
                            {opp.spa_linked ? <SpaBadge size="sm" /> : '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => approveOpportunity(opp.id)}
                                disabled={actionLoading === opp.id}
                              >
                                {actionLoading === opp.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <CheckCircle className="mr-1 h-4 w-4" />
                                )}
                                {t('admin.dashboard.approve')}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(`/opportunities/${opp.id}`)}
                              >
                                {t('common.view')}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="programs">
            <ProgramsTab />
          </TabsContent>

          <TabsContent value="documents">
            <DocumentsTab />
          </TabsContent>

          <TabsContent value="events">
            <EventsTab />
          </TabsContent>

          <TabsContent value="diaspora">
            <DiasporaTab />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}